import forEach from 'lodash/forEach';
import get from 'lodash/get';
import startsWith from 'lodash/startsWith';
import uniq from 'lodash/uniq';
import uniqueId from 'lodash/uniqueId';

export function createFixedLayerId(): string {
    return uniqueId('__fixed');
}

// This function merges an array of Mapbox styles into one Mapbox style.

export default function concatMapboxStyles(inputs: GaiaMapboxStyle[]): GaiaMapboxStyle {
    const output: GaiaMapboxStyle = {
        version: 8,
        sources: {},
        layers: [],
    };

    const uniqueLayers: {[layerId: string]: boolean} = {};

    forEach(inputs, (input: GaiaMapboxStyle, index: number) => {
        const layerType = input.metadata as LayerType | undefined;
        const prefix = layerType && layerType.id !== 'selectionStyle' ? `${layerType.id}__` : '';

        // Take all sources and prefix their ids
        forEach(input.sources, (source: mapboxgl.AnySourceData, id: string) => {
            output.sources![prefix + id] = source;
        });

        // Take all layers and prefix their ids
        forEach(input.layers, (layer: any) => {
            const id = `${startsWith(layer.id, '__fixed') ? '' : prefix}${layer.id}`;

            if (layer.type !== 'background' && !uniqueLayers[id]) {
                output.layers!.push({
                    ...layer,
                    id,
                    source: `${prefix}${layer.source}`,
                });
                uniqueLayers[id] = true;
            } else {
                // If we encounter an opaque background layer, we discard all layers beneath it
                // as a performance optimization.
                const opacity = get(layer, 'paint.background-opacity', 1);
                if (layerType && layerType.basemap && opacity === 1) {
                    output.layers = [];
                    output.glyphs = undefined;
                    output.sprite = undefined;
                }

                // We omit "background-pattern" layers because many Mapbox GL styles have a
                // loading grid pattern that becomes visible if a layer is translucent.
                if ((index === 0 || !get(layer, 'paint.background-pattern')) && !uniqueLayers[id]) {
                    output.layers!.push({...layer, id});
                    uniqueLayers[id] = true;
                }
            }
        });

        // "details-template" and "interaction-layers" are an interface we've invented at Gaia as
        // a standard way to show simple popups on map layers.
        if (input['details-template'] && input['interaction-layers']) {
            output['interaction-layers'] = output['interaction-layers'] || [];
            output['details-template'] = output['details-template'] || {};

            forEach(input['interaction-layers'], (id) => {
                output['interaction-layers']!.push(prefix + id);
            });

            if (input['details-template']['*']) {
                forEach(input['interaction-layers'], (id) => {
                    output['details-template']![prefix + id] = input['details-template']!['*'];
                });
            } else if (input['details-template']) {
                forEach(input['details-template'], (template, id) => {
                    output['details-template']![prefix + id] = template;
                });
            }
        }

        // Take the topmost zoom and center
        if (input.zoom) output.zoom = input.zoom;
        if (input.center) output.center = input.center;

        // Take the bottommost glyphs and sprite
        if (input.glyphs && !output.glyphs) output.glyphs = input.glyphs;
        if (input.sprite && !output.sprite) output.sprite = input.sprite;

        // Add the topmost terrain and prefix the source
        if (input.terrain) {
            output.terrain = {
                ...input.terrain,
                source: prefix + input.terrain.source,
            };
        }

        // Add the topmost fog
        if (input.fog) output.fog = input.fog;
    });

    // Dedupe interaction-layers
    if (output['interaction-layers']) {
        output['interaction-layers'] = uniq(output['interaction-layers'] || []);
    }

    return output;
}
